/**
 * ThemeContext — ERP color-scheme manager.
 *
 * Five hand-tuned themes are supported: dark (default Door O), light, ocean, indigo, carbon.
 *
 * Resolution order on mount:
 *   1. localStorage["door_o_theme"]   (instant — set before AuthContext loads, no flicker)
 *   2. user.theme                     (server preference once /auth/me resolves)
 *   3. "dark"                          (final fallback)
 *
 * The active theme is applied as `data-theme="<value>"` on the document root by ThemeBridge.
 * Tailwind utility classes are then overridden globally via src/styles/themes.css, scoped to
 * `[data-theme="X"] [data-erp-shell] …` selectors so the public marketing site is unaffected.
 *
 * When `setTheme(t)` is called it:
 *   - updates context state,
 *   - writes to localStorage (instant persistence + no-flicker on next page load),
 *   - and PATCHes /api/auth/me/theme when a user is signed in (best-effort, never throws to UI).
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export const THEMES = [
  { id: "dark",    name: "Door O Dark",     swatches: ["#030712", "#111827", "#f97316"], desc: "Original Control-Room aesthetic — high contrast, focus mode." },
  { id: "light",   name: "Enterprise Light", swatches: ["#f8fafc", "#1e293b", "#0F4FA8"], desc: "Modern SaaS — dark navy sidebar, white tables, deep-blue accents. Easy on the eyes for long shifts." },
  { id: "ocean",   name: "Ocean Blue",      swatches: ["#0b1626", "#0f2a4a", "#38bdf8"], desc: "Deep navy with sky-blue accents. Calm and seaworthy." },
  { id: "indigo",  name: "Midnight Indigo", swatches: ["#0f0f23", "#1e1b3a", "#818cf8"], desc: "Twilight purple — modern, low-glare night theme." },
  { id: "carbon",  name: "Carbon",          swatches: ["#0a0a0a", "#1a1a1a", "#10b981"], desc: "Pure carbon-black with emerald accents. Minimal & sharp." },
];

const VALID_IDS = THEMES.map((t) => t.id);
const STORAGE_KEY = "door_o_theme";

const readInitialTheme = () => {
  if (typeof window === "undefined") return null;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (VALID_IDS.includes(stored)) return stored;
  } catch (_) { /* localStorage unavailable */ }
  return null; // null = "no explicit local pick" → let serverTheme win once it arrives
};

const ThemeContext = createContext({ theme: "dark", setTheme: () => {}, themes: THEMES });

export function ThemeProvider({ children }) {
  const { user } = useAuth();
  // Local override (takes priority over server value when explicitly set).
  // null = no explicit local pick yet, so server preference is honoured.
  const [localOverride, setLocalOverride] = useState(readInitialTheme);

  // Derived "active" theme: explicit local pick > server pref > dark default.
  const serverTheme = VALID_IDS.includes(user?.theme) ? user.theme : null;
  const theme = localOverride ?? serverTheme ?? "dark";

  // Mirror to <html data-theme="...">
  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.setAttribute("data-theme", theme);
  }, [theme]);

  // Mirror server pref into localStorage so cold reloads boot in the right theme without flicker.
  useEffect(() => {
    if (serverTheme) {
      try { localStorage.setItem(STORAGE_KEY, serverTheme); } catch (_) { /* no-op */ }
    }
  }, [serverTheme]);

  const setTheme = useCallback(
    async (t) => {
      if (!VALID_IDS.includes(t)) return;
      setLocalOverride(t);
      try { localStorage.setItem(STORAGE_KEY, t); } catch (_) { /* no-op */ }
      try {
        if (user) await api.patch("/auth/me/theme", { theme: t });
      } catch (_) { /* best-effort, local already updated */ }
    },
    [user]
  );

  return <ThemeContext.Provider value={{ theme, setTheme, themes: THEMES }}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}
